import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  TrendingUp,
  AlertCircle,
  Clock,
  Edit3,
  Sparkles,
  Search,
} from "lucide-react";
import apiClient from "../../lib/api-client";

interface VoiceAiOrgItem {
  locationId: string;
  locationName: string;
  city: string;
  phoneNumber: string | null;
  organizationId: string;
  organizationName: string;
  subscriptionStatus: string;
  hasCustomPrompt: boolean;
  promptUpdatedAt: string | null;
  metrics: {
    totalCalls: number;
    reservationsCreated: number;
    successRate: number;
    problematicCalls: number;
    pendingAnalysis: number;
    totalDurationSec: number;
    totalCost: number;
  };
}

export default function VoiceAiAdmin() {
  const [items, setItems] = useState<VoiceAiOrgItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setLoading(true);
    apiClient
      .get(`/admin/voice-ai/organizations?days=${days}`)
      .then((res) => setItems(res.data.data.organizations))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [days]);

  const filtered = items.filter((i) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      i.locationName.toLowerCase().includes(q) ||
      i.organizationName.toLowerCase().includes(q) ||
      i.city.toLowerCase().includes(q) ||
      (i.phoneNumber && i.phoneNumber.includes(q))
    );
  });

  const totalCalls = items.reduce((s, i) => s + i.metrics.totalCalls, 0);
  const totalProblematic = items.reduce(
    (s, i) => s + i.metrics.problematicCalls,
    0,
  );
  const totalReservations = items.reduce(
    (s, i) => s + i.metrics.reservationsCreated,
    0,
  );
  const totalCost = items.reduce((s, i) => s + i.metrics.totalCost, 0);

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Phone className="w-6 h-6 text-indigo-600" />
            Cerebro IA · Panel Superadmin
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Visión global de todas las centralitas IA. Ordenado por tasa de
            éxito ascendente para detectar problemas.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs rounded-lg border ${
                days === d
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Últimos {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPIs globales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">Restaurantes activos</p>
          <p className="text-2xl font-bold text-gray-900">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">Llamadas totales</p>
          <p className="text-2xl font-bold text-gray-900">{totalCalls}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">Reservas creadas</p>
          <p className="text-2xl font-bold text-green-700">
            {totalReservations}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-3">
          <p className="text-xs text-gray-500">Coste total</p>
          <p className="text-2xl font-bold text-gray-900">
            ${totalCost.toFixed(2)}
          </p>
          <p className="text-xs text-amber-700 mt-1">
            {totalProblematic} problemáticas
          </p>
        </div>
      </div>

      {/* Búsqueda */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-4 flex items-center gap-2">
        <Search className="w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por restaurante, organización, ciudad o teléfono..."
          className="flex-1 outline-none text-sm"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
          No hay restaurantes con Voice AI activado
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left">
                <th className="px-4 py-3 font-medium text-gray-700">
                  Restaurante
                </th>
                <th className="px-3 py-3 font-medium text-gray-700">
                  Llamadas
                </th>
                <th className="px-3 py-3 font-medium text-gray-700">Éxito</th>
                <th className="px-3 py-3 font-medium text-gray-700">
                  Reservas
                </th>
                <th className="px-3 py-3 font-medium text-gray-700">
                  Problemas
                </th>
                <th className="px-3 py-3 font-medium text-gray-700">Pdte IA</th>
                <th className="px-3 py-3 font-medium text-gray-700">Coste</th>
                <th className="px-3 py-3 font-medium text-gray-700">Prompt</th>
                <th className="px-3 py-3 font-medium text-gray-700"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((it) => {
                const sr = it.metrics.successRate;
                const srColor =
                  it.metrics.totalCalls === 0
                    ? "text-gray-400"
                    : sr >= 70
                      ? "text-green-700"
                      : sr >= 40
                        ? "text-amber-700"
                        : "text-red-700";
                return (
                  <tr key={it.locationId} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {it.locationName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {it.organizationName} · {it.city}
                          {it.phoneNumber && ` · ${it.phoneNumber}`}
                        </p>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-gray-900">
                      {it.metrics.totalCalls}
                    </td>
                    <td className={`px-3 py-3 font-semibold ${srColor}`}>
                      {it.metrics.totalCalls === 0 ? "—" : `${sr}%`}
                    </td>
                    <td className="px-3 py-3 text-green-700">
                      {it.metrics.reservationsCreated}
                    </td>
                    <td className="px-3 py-3">
                      {it.metrics.problematicCalls > 0 ? (
                        <span className="inline-flex items-center gap-1 text-red-700">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {it.metrics.problematicCalls}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      {it.metrics.pendingAnalysis > 0 ? (
                        <span className="inline-flex items-center gap-1 text-indigo-700">
                          <Clock className="w-3.5 h-3.5" />
                          {it.metrics.pendingAnalysis}
                        </span>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-gray-700">
                      ${it.metrics.totalCost.toFixed(2)}
                    </td>
                    <td className="px-3 py-3">
                      {it.hasCustomPrompt ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700">
                          <Edit3 className="w-3 h-3" />
                          Personalizado
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Auto</span>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <Link
                        to={`/admin/voice-ai/${it.locationId}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                      >
                        <TrendingUp className="w-3.5 h-3.5" />
                        Detalle
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500 flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
        Pulsa <strong>Detalle</strong> para analizar las llamadas problemáticas
        con IA y editar el prompt de ese restaurante.
      </div>
    </div>
  );
}
